import { FilterFn, RowData } from "@tanstack/react-table";
import { format, isValid, parse } from "date-fns";
import { Creator } from "@/types/types";
import { formatCompact } from "@/lib/format";

// Per-column filtering for the creators table. A column opts in by declaring
// `meta.filter` (what kind of editor to show) and using the matching filterFn;
// the ColumnFilter popover lists every column that did.

export type ColumnFilterKind = "text" | "number" | "select" | "date";

export interface ColumnFilterMeta {
  kind: ColumnFilterKind;
  /** Name in the column picker (headers are often tooltip components, not strings). */
  label: string;
  /** select: raw cell value (missing arrives as "") → checklist label. */
  format?: (value: string) => string;
  /** number: raw × scale is what the user sees and types (eng. rate 0.023 → 2.3 %). */
  scale?: number;
  /** number: unit hint after the inputs ("%", "USD"). */
  unit?: string;
  /** text: input placeholder. */
  placeholder?: string;
  /**
   * select: keys the checklist always offers, even when no loaded row carries
   * them — the org-wide table only holds one page, so a CRM boolean's
   * accepted / rejected / missing rows come from here, not from the data.
   */
  options?: string[];
  /**
   * select: explicit option order (keys). Listed keys come first in this
   * order; anything else follows alphabetically — the Campaign checklist
   * lists campaigns newest first, like the campaigns page and the sidebar.
   */
  order?: string[];
}

export interface NumberRangeFilter {
  /** Raw (unscaled) bounds, inclusive. */
  min?: number;
  max?: number;
}

export interface DateRangeFilter {
  /** ISO days (yyyy-MM-dd, what date CRM fields store), inclusive. */
  from?: string;
  to?: string;
}

export const ISO_DAY_FMT = "yyyy-MM-dd";
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseIsoDay(iso?: string): Date | undefined {
  if (!iso) return undefined;
  const d = parse(iso, ISO_DAY_FMT, new Date());
  return isValid(d) ? d : undefined;
}

export function formatIsoDay(d: Date): string {
  return format(d, ISO_DAY_FMT);
}

/** "Sep 1", or "Sep 1, 2025" when it's not this year. */
function shortDay(iso: string): string {
  const d = parseIsoDay(iso);
  if (!d) return iso;
  return format(d, d.getFullYear() === new Date().getFullYear() ? "MMM d" : "MMM d, yyyy");
}

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filter?: ColumnFilterMeta;
    /** Start hidden (until the user toggles it in View) — org-wide extras like phase columns. */
    defaultHidden?: boolean;
  }
}

/** Faceted values are raw cell values; null/undefined/"" all mean "missing". */
export function selectKey(value: unknown): string {
  return value == null ? "" : String(value);
}

/** Checklist label for a select option — the column's formatter, "Missing" for empties. */
export function optionLabel(meta: ColumnFilterMeta, key: string): string {
  const label = meta.format ? meta.format(key) : key;
  return label || "Missing";
}

/** Case-insensitive "contains" on the cell's string value. */
export const textFilterFn: FilterFn<Creator> = (row, columnId, filterValue) => {
  const term = String(filterValue ?? "").trim().toLowerCase();
  if (!term) return true;
  return String(row.getValue(columnId) ?? "")
    .toLowerCase()
    .includes(term);
};
textFilterFn.autoRemove = (value) => !String(value ?? "").trim();

/** Inclusive min/max on the raw number; rows without a number never match a range. */
export const numberRangeFilterFn: FilterFn<Creator> = (
  row,
  columnId,
  filterValue: NumberRangeFilter | undefined
) => {
  const min = filterValue?.min;
  const max = filterValue?.max;
  if (min == null && max == null) return true;
  const raw = row.getValue(columnId);
  const n =
    typeof raw === "number" ? raw : raw == null || raw === "" ? NaN : Number(raw);
  if (Number.isNaN(n)) return false;
  if (min != null && n < min) return false;
  if (max != null && n > max) return false;
  return true;
};
numberRangeFilterFn.autoRemove = (value: NumberRangeFilter | undefined) =>
  value == null || (value.min == null && value.max == null);

/** Inclusive day range on an ISO-day cell; rows without a valid day never match. */
export const dateRangeFilterFn: FilterFn<Creator> = (
  row,
  columnId,
  filterValue: DateRangeFilter | undefined
) => {
  const from = filterValue?.from;
  const to = filterValue?.to;
  if (!from && !to) return true;
  const day = String(row.getValue(columnId) ?? "").trim();
  if (!ISO_DAY_RE.test(day)) return false;
  // ISO days compare correctly as strings.
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
};
dateRangeFilterFn.autoRemove = (value: DateRangeFilter | undefined) =>
  value == null || (!value.from && !value.to);

/** Checklist: the row's value (as a select key) must be one of the picked keys. */
export const selectFilterFn: FilterFn<Creator> = (
  row,
  columnId,
  filterValue: string[] | undefined
) => {
  if (!filterValue?.length) return true;
  return filterValue.includes(selectKey(row.getValue(columnId)));
};
selectFilterFn.autoRemove = (value: string[] | undefined) => !value?.length;

/** "10k" → 10000, "1.5m" → 1500000, "$1,200" → 1200, "2.5%" → 2.5. Anything else → undefined. */
export function parseFilterNumber(input: string): number | undefined {
  const cleaned = input.trim().toLowerCase().replace(/[\s,$€£%]/g, "");
  const match = /^(-?\d*\.?\d+)([km])?$/.exec(cleaned);
  if (!match) return undefined;
  const n = parseFloat(match[1]);
  if (Number.isNaN(n)) return undefined;
  return match[2] === "k" ? n * 1_000 : match[2] === "m" ? n * 1_000_000 : n;
}

/** Exact, for the range inputs: 12345 → "12,345", 2.5 → "2.5". */
export function formatFilterInput(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 4 });
}

/** Compact, for summaries and placeholders: 12345 → "12.3K", 2.25 → "2.25". */
export function formatFilterCompact(n: number): string {
  return Math.abs(n) >= 1_000 ? formatCompact(n) : Number(n.toFixed(2)).toString();
}

/** One-line description of an active filter for the column list; null when unset. */
export function summarizeFilter(meta: ColumnFilterMeta, value: unknown): string | null {
  if (value == null) return null;
  switch (meta.kind) {
    case "text": {
      const term = String(value).trim();
      return term ? `“${term}”` : null;
    }
    case "number": {
      const { min, max } = value as NumberRangeFilter;
      const scale = meta.scale ?? 1;
      const unit = meta.unit ? (meta.unit === "%" ? "%" : ` ${meta.unit}`) : "";
      const fmt = (n: number) => formatFilterCompact(n * scale);
      if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}${unit}`;
      if (min != null) return `≥ ${fmt(min)}${unit}`;
      if (max != null) return `≤ ${fmt(max)}${unit}`;
      return null;
    }
    case "date": {
      const { from, to } = value as DateRangeFilter;
      if (from && to) return `${shortDay(from)} – ${shortDay(to)}`;
      if (from) return `from ${shortDay(from)}`;
      if (to) return `until ${shortDay(to)}`;
      return null;
    }
    case "select": {
      const keys = value as string[];
      if (!keys.length) return null;
      if (keys.length === 1) return optionLabel(meta, keys[0]);
      return `${keys.length} selected`;
    }
  }
}
